/**
 * Geolocation utility for getting device coordinates
 * Handles permission requests, errors, and caching
 */

interface GeolocationCache {
  coordinates: string; // "latitude,longitude"
  timestamp: number;
}

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour
const CACHE_KEY = 'device-geolocation-cache';

/**
 * Get device geolocation coordinates
 * @returns Promise resolving to "latitude,longitude" string or null if unavailable
 */
export async function getDeviceLocation(): Promise<string | null> {
  // Check if geolocation is available
  if (!navigator.geolocation) {
    console.warn('[geolocation] Geolocation is not supported by this browser');
    return null;
  }

  // Check cache first
  const cached = getCachedLocation();
  if (cached) {
    console.log('[geolocation] Using cached device location');
    return cached;
  }

  // Request current location
  return new Promise((resolve) => {
    const options: PositionOptions = {
      enableHighAccuracy: false, // Use less accurate but faster method
      timeout: 10000, // 10 second timeout
      maximumAge: 5 * 60 * 1000, // Accept cached position up to 5 minutes old
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const coordinates = `${latitude},${longitude}`;
        
        // Cache the location
        cacheLocation(coordinates);
        
        console.log('[geolocation] Successfully obtained device location');
        resolve(coordinates);
      },
      (error) => {
        console.warn('[geolocation] Error getting device location:', error.message);
        
        // Handle different error types
        switch (error.code) {
          case error.PERMISSION_DENIED:
            console.warn('[geolocation] User denied geolocation permission');
            break;
          case error.POSITION_UNAVAILABLE:
            console.warn('[geolocation] Location information unavailable');
            break;
          case error.TIMEOUT:
            console.warn('[geolocation] Location request timed out');
            break;
        }
        
        resolve(null);
      },
      options
    );
  });
}

/**
 * Get cached device location if still valid
 */
function getCachedLocation(): string | null {
  try {
    const cachedStr = localStorage.getItem(CACHE_KEY);
    if (!cachedStr) return null;

    const cached: GeolocationCache = JSON.parse(cachedStr);
    const now = Date.now();
    const age = now - cached.timestamp;

    if (age < CACHE_DURATION) {
      return cached.coordinates;
    }

    // Cache expired, remove it
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch (error) {
    console.error('[geolocation] Error reading cache:', error);
    return null;
  }
}

/**
 * Cache device location
 */
function cacheLocation(coordinates: string): void {
  try {
    const cache: GeolocationCache = {
      coordinates,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('[geolocation] Error caching location:', error);
  }
}

/**
 * Clear cached device location
 */
export function clearDeviceLocationCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.error('[geolocation] Error clearing cache:', error);
  }
}
